var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PHOTO_URL_CHECK_INTERVAL_MS, PHOTO_URL_EMPTY_CACHE_TTL_MS, PHOTO_URL_FALLBACK_EXPIRY_MIN, PHOTO_URL_REFRESH_BUFFER_MS } from "../constants/profilePhotoConstants";
import { isCacheValid, parseExpiryMinutes } from "../utils/imageUtils";
import { httpDelete, httpGet, httpPost, httpPut } from "./request/requestApi";
const endPointPath = "auth";
let profilePhotoUrlCache = null;
let autoRefreshInterval = null;
let isRefreshing = false;
let onUrlRefreshed = null;
let lastNotifiedUrl = null;
export class AccountApi {
    static signIn(data) {
        return __awaiter(this, void 0, void 0, function* () {
            return httpPost(`${endPointPath}/authenticate`, { body: data });
        });
    }
    static fetchUsernameByToken(username, silent = true) {
        return __awaiter(this, void 0, void 0, function* () {
            return httpGet(`${endPointPath}/fetchUsernameByToken/${username}`, {
                silent,
                headers: {
                    Accept: 'text/plain',
                },
            });
        });
    }
    static uploadProfilePhoto(file) {
        return __awaiter(this, void 0, void 0, function* () {
            const formData = new FormData();
            formData.append("file", file);
            const res = yield httpPost(`${endPointPath}/photo`, { body: formData });
            profilePhotoUrlCache = null;
            return res;
        });
    }
    static getProfilePhotoUrl(silent = true) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const res = yield httpGet(`${endPointPath}/photo/url`, { silent });
                return res;
            }
            catch (err) {
                const status = (err === null || err === void 0 ? void 0 : err.status) || ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status);
                if (status === 404)
                    return { url: null, expiryMessage: "No profile photo found" };
                throw err;
            }
        });
    }
    static getProfilePhotoUrlCached(silent = true, bufferMs = PHOTO_URL_REFRESH_BUFFER_MS, force = false) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (isRefreshing && !force) {
                return {
                    url: (_a = profilePhotoUrlCache === null || profilePhotoUrlCache === void 0 ? void 0 : profilePhotoUrlCache.url) !== null && _a !== void 0 ? _a : null,
                    expiryMessage: "refreshing"
                };
            }
            if (!force && isCacheValid(profilePhotoUrlCache, bufferMs)) {
                return { url: profilePhotoUrlCache.url, expiryMessage: "cached" };
            }
            try {
                isRefreshing = true;
                const res = yield this.getProfilePhotoUrl(silent);
                if (!(res === null || res === void 0 ? void 0 : res.url)) {
                    profilePhotoUrlCache = {
                        url: null,
                        expiresAtMs: Date.now() + PHOTO_URL_EMPTY_CACHE_TTL_MS,
                    };
                    if (onUrlRefreshed && lastNotifiedUrl !== null) {
                        lastNotifiedUrl = null;
                        onUrlRefreshed(null);
                    }
                    return res;
                }
                const mins = (_b = parseExpiryMinutes(res === null || res === void 0 ? void 0 : res.expiryMessage)) !== null && _b !== void 0 ? _b : PHOTO_URL_FALLBACK_EXPIRY_MIN;
                profilePhotoUrlCache = {
                    url: res.url,
                    expiresAtMs: Date.now() + mins * 60000,
                };
                if (onUrlRefreshed && lastNotifiedUrl !== res.url) {
                    lastNotifiedUrl = res.url;
                    onUrlRefreshed(res.url);
                }
                return res;
            }
            finally {
                isRefreshing = false;
            }
        });
    }
    static getProfilePhotoUrlAutoRefresh(silent = true, bufferMs = PHOTO_URL_REFRESH_BUFFER_MS, force = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.getProfilePhotoUrlCached(silent, bufferMs, force);
            return (_a = res === null || res === void 0 ? void 0 : res.url) !== null && _a !== void 0 ? _a : null;
        });
    }
    static startProfilePhotoUrlAutoRefresh(options) {
        var _a, _b, _c, _d;
        if (autoRefreshInterval !== null) {
            onUrlRefreshed = (options === null || options === void 0 ? void 0 : options.onRefresh) || null;
            return;
        }
        const silent = (_a = options === null || options === void 0 ? void 0 : options.silent) !== null && _a !== void 0 ? _a : true;
        const bufferMs = (_b = options === null || options === void 0 ? void 0 : options.bufferMs) !== null && _b !== void 0 ? _b : PHOTO_URL_REFRESH_BUFFER_MS;
        const refreshCheckEveryMs = (_c = options === null || options === void 0 ? void 0 : options.refreshCheckEveryMs) !== null && _c !== void 0 ? _c : PHOTO_URL_CHECK_INTERVAL_MS;
        onUrlRefreshed = (options === null || options === void 0 ? void 0 : options.onRefresh) || null;
        lastNotifiedUrl = (_d = profilePhotoUrlCache === null || profilePhotoUrlCache === void 0 ? void 0 : profilePhotoUrlCache.url) !== null && _d !== void 0 ? _d : null;
        autoRefreshInterval = window.setInterval(() => __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.getProfilePhotoUrlCached(silent, bufferMs);
            }
            catch (_e) {
                // Optionally handle errors, e.g., log them
            }
        }), refreshCheckEveryMs);
    }
    static stopProfilePhotoUrlAutoRefresh() {
        if (autoRefreshInterval !== null) {
            window.clearInterval(autoRefreshInterval);
            autoRefreshInterval = null;
            onUrlRefreshed = null;
            lastNotifiedUrl = null;
        }
    }
    static forceRefreshProfilePhotoUrl(silent = true) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getProfilePhotoUrlAutoRefresh(silent, PHOTO_URL_REFRESH_BUFFER_MS, true);
        });
    }
    static deleteProfilePhoto() {
        return __awaiter(this, void 0, void 0, function* () {
            yield httpDelete(`${endPointPath}/photo`);
            profilePhotoUrlCache = null;
            return { message: "Photo deleted successfully" };
        });
    }
    static logout() {
        return __awaiter(this, void 0, void 0, function* () {
            return httpPost(`${endPointPath}/logout`);
        });
    }
    static loadContext() {
        return httpGet(`${endPointPath}/userDetails`);
    }
    static forgetPassword(username) {
        return httpPost(`${endPointPath}/forgetPassword/${username}`);
    }
    static changePassword(data) {
        return httpPost(`${endPointPath}/changePassword`, { body: data });
    }
    static storeContext(data) {
        return httpPut(`${endPointPath}/saveContext`, { body: data });
    }
    static refreshToken() {
        return httpGet(`${endPointPath}/refreshToken`, {
            requestHeader: true,
        });
    }
}
